import Vue, { onBeforeUnmount, ref, type Component, type Ref } from 'vue';
import VueRouter, { type RawLocation, type Route, type RouteConfig } from 'vue-router';
import type { FlipDirection, FlipEdge, FlipLayout } from '../ui/flipView';
import FlipView from '../ui/FlipView.vue';

Vue.use(VueRouter);

export interface UseFlipRouter {
  RouterView: Component;
  router: VueRouter;
  route: Ref<Route>;
  direction: Ref<FlipDirection>;
  edge: Ref<FlipEdge>;
  push: (location: RawLocation) => void;
  replace: (location: RawLocation) => void;
  back: () => void;
}

export interface FlipRoute {
  path: string;
  name?: string;
  edge?: FlipEdge;
  component: unknown;
  props?: RouteConfig['props'];
}

export interface UseFlipRouterOptions {
  routes: FlipRoute[];
  initial?: RawLocation;
  layout?: FlipLayout;
}

function noop(): void {
  return undefined;
}

export function useFlipRouter(options: UseFlipRouterOptions): UseFlipRouter {
  const router = new VueRouter({ mode: 'abstract', routes: options.routes as RouteConfig[] });
  const direction = ref<FlipDirection>('forward');
  const edge = ref<FlipEdge>('right');
  const route = ref<Route>(router.currentRoute) as Ref<Route>;
  const layout = options.layout ?? 'flow';

  const readIndex = (): number => {
    const history = (router as unknown as { history?: { index?: number } }).history;
    return typeof history?.index === 'number' ? history.index : 0;
  };
  let prevIndex = readIndex();

  const edgeOf = (target: Route): FlipEdge => options.routes.find((r) => r.name === target.name)?.edge ?? 'right';

  const stopAfterEach = router.afterEach((to: Route, from: Route) => {
    const nextIndex = readIndex();
    if (nextIndex > prevIndex) {
      direction.value = 'forward';
      edge.value = edgeOf(to);
    } else {
      direction.value = 'back';
      edge.value = edgeOf(from);
    }
    prevIndex = nextIndex;
    route.value = to;
  });

  const start = options.initial ?? options.routes[0]?.path ?? '/';
  router.push(start, noop, noop);
  prevIndex = readIndex();
  route.value = router.currentRoute;

  const push = (location: RawLocation): void => {
    router.push(location, noop, noop);
  };

  const replace = (location: RawLocation): void => {
    router.replace(location, noop, noop);
  };

  const back = (): void => {
    router.back();
  };

  onBeforeUnmount(() => {
    stopAfterEach();
  });

  const RouterView: Component = Vue.extend({
    name: 'FlipRouterHost',
    router,
    render(createElement) {
      return createElement(FlipView, {
        props: { direction: direction.value, edge: edge.value, layout },
        scopedSlots: {
          default: () =>
            createElement('router-view', {
              key: route.value.name || route.value.fullPath,
              scopedSlots: this.$scopedSlots,
            }),
        },
      });
    },
  });

  return { RouterView, router, route, direction, edge, push, replace, back };
}
